import type { AssociatedParty } from '@/shared/hooks/api/associatedParties'

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

export type AddressFormatFields = {
  street?: string | null
  streetNumber?: string | null
  street2?: string | null
  postCode?: string | null
  city?: string | null
  subDivision?: string | null
  country?: string | null
  poBox?: string | null
}

type AddressFormatOptions = {
  getCountryName: (countryCode: string) => string
  translate: TranslateFn
}

export function formatAddressFields(
  fields: AddressFormatFields,
  options: AddressFormatOptions
): string {
  const parts: string[] = []
  const street = [fields.street, fields.streetNumber].filter(Boolean).join(' ')
  if (street.trim()) {
    parts.push(street.trim())
  }
  if (fields.street2?.trim()) {
    parts.push(fields.street2.trim())
  }
  const cityLine = [fields.postCode, fields.city].filter(Boolean).join(' ')
  if (cityLine.trim()) {
    parts.push(cityLine.trim())
  }
  if (fields.subDivision?.trim()) {
    parts.push(fields.subDivision.trim())
  }
  if (fields.country?.trim()) {
    parts.push(options.getCountryName(fields.country.trim()))
  }
  if (fields.poBox?.trim()) {
    parts.push(
      options.translate('address.poBox', {
        value: fields.poBox.trim(),
      })
    )
  }
  return parts.join(', ') || options.translate('emptyValue')
}

export function formatAssociatedPartyAddress(
  party: AssociatedParty,
  options: AddressFormatOptions
): string {
  return formatAddressFields(
    {
      street: party.street,
      streetNumber: party.streetNumber,
      street2: party.street2,
      postCode: party.postCode,
      city: party.city,
      subDivision: party.subDivision,
      country: party.country,
      poBox: party.poBox,
    },
    options
  )
}

type PartyCityCountryFields = {
  city?: string | null
  country?: string | null
}

/**
 * Compact "City, Country" label for pickers and cards (not full address).
 */
export function formatPartyCityCountry(
  party: PartyCityCountryFields,
  getCountryName: (countryCode: string) => string
): string | null {
  const parts: string[] = []
  const city = party.city?.trim()
  if (city) {
    parts.push(city)
  }
  const countryCode = party.country?.trim()
  if (countryCode) {
    parts.push(getCountryName(countryCode))
  }
  return parts.length > 0 ? parts.join(', ') : null
}
